import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { slide as Menu } from 'react-burger-menu';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({}); // Track expanded state for each menu item
  const navRef = useRef(null);

  // Handle menu state changes
  const handleStateChange = (state) => {
    setMenuOpen(state.isOpen);
  };

  // Close menu
  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Toggle expanded state for a menu item in mobile view
  const toggleExpanded = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // ...existing code...

  // Styles for react-burger-menu
  const burgerStyles = {
    bmBurgerButton: {
      display: 'none', // We'll use our own button
    },
    bmBurgerBars: {
      background: '#373a47'
    },
    bmBurgerBarsHover: {
      background: '#a90000'
    },
    bmCrossButton: {
      height: '24px',
      width: '24px'
    },
    bmCross: {
      background: '#bdc3c7'
    },
    bmMenuWrap: {
      position: 'fixed',
      top: '64px', // Match the height of the navbar
      height: 'calc(100% - 64px)'
    },
    bmMenu: {
      background: 'white',
      padding: '1em 0',
      fontSize: '1.15em',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    bmMorphShape: {
      fill: '#373a47'
    },
    bmItemList: {
      color: '#b8b7ad',
      padding: '0.8em'
    },
    bmItem: {
      display: 'block',
      outline: 'none'
    },
    bmOverlay: {
      background: 'rgba(0, 0, 0, 0.3)'
    }
  };

  // Menu structure split into left and right sections
  const leftMenuItems = [
    {
      title: "Our Audit",
      href: "#",
      submenu: [
        { title: "Get Protected", href: "/get-protected" },
        { title: "Stay Compliant", href: "/stay-compliant" },
        { title: "Our Offers", href: "/how-we-help" }
      ]
    },
    {
      title: "Our Company",
      href: "#",
      submenu: [
        { title: "Why Choose Us", href: "/why-choose-us" },
        { title: "Our Founder", href: "/our-founder" },
        { title: "PQC in the News", href: "/pqc-in-the-news" }
      ]
    },
    {
      title: "Our Products",
      href: "#",
      submenu: [
        { title: "Kemforge", href: "/kemforge" },
        { title: "Quantum TLS Transformer", href: "/quantum-tls-transformer" }
      ]
    },
    {
      title: "FAQ",
      href: "/faq"
    }
    ,
    {
      title: "Packages for developers",
      href: "/packages-for-developers"
    }
  ];

  const rightMenuItems = [
    {
      title: "Privacy Policy",
      href: "/privacy-policy"
    },
    {
      title: "Contact Us",
      href: "/contact-us",
      isButton: true // Special styling for this item
    }
  ];

  // Combined menu items for mobile view
  const menuItems = [...leftMenuItems, ...rightMenuItems];

  // Create mobile menu items with proper styling and functionality
  const renderMobileMenuItems = () => {
    return (
      <div className="space-y-4">
        {menuItems.map((item, index) => (
          <div key={index} className="border-b border-gray-200 pb-4">
            {/* Main menu item */}
            <div className="flex items-center justify-between">
              <Link 
                to={item.href}
                className={classNames(
                  "text-gray-700 font-medium text-base",
                  item.isButton && "text-blue-600 font-semibold"
                )}
                onClick={closeMenu}
              >
                {item.title}
              </Link>

              {item.submenu && (
                <button
                  className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={() => toggleExpanded(index)}
                  aria-expanded={expandedItems[index] || false}
                >
                  <svg 
                    className={`h-5 w-5 transform transition-transform duration-200 ${expandedItems[index] ? 'rotate-180' : ''}`} 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Submenu items */}
            {item.submenu && expandedItems[index] && (
              <div className="mt-2 ml-4 space-y-2">
                {item.submenu.map((subItem, subIndex) => (
                  <Link 
                    key={subIndex}
                    to={subItem.href}
                    className="block py-2 text-gray-600 hover:text-gray-900"
                    onClick={closeMenu}
                  >
                    {subItem.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav ref={navRef} className="flex justify-between items-center h-16">
          {/* Logo and Left Menu Items */}
          <div className="flex items-center">
            {/* Logo */}
            <div className="flex-shrink-0 mr-4">
              <Link to="/" className="flex items-center">
                <img 
                  src="/logo192.png" 
                  alt="Quantum Safe Audit" 
                  className="h-8 w-auto" 
                />
              </Link>
            </div>

            {/* Left Desktop Navigation */}
            <div className="hidden md:block">
              <ul className="flex space-x-6">
                {leftMenuItems.map((item, index) => (
                  <li key={index} className="relative group">
                    <Link 
                      to={item.href}
                      className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium"
                    >
                      {item.title}
                    </Link>

                    {item.submenu && (
                      <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 opacity-0 group-hover:opacity-100 invisible group-hover:visible dropdown-menu">
                        {item.submenu.map((subItem, subIndex) => (
                          <Link 
                            key={subIndex}
                            to={subItem.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Desktop Navigation */}
          <div className="hidden md:block">
            <ul className="flex space-x-8">
              {rightMenuItems.map((item, index) => (
                <li key={index} className="relative group">
                  <Link 
                    to={item.href}
                    className={classNames(
                      "text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium",
                      item.isButton && "bg-blue-600 text-white rounded-md hover:bg-blue-700 px-4"
                    )}
                  >
                    {item.title}
                  </Link>

                  {item.submenu && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 opacity-0 group-hover:opacity-100 invisible group-hover:visible dropdown-menu">
                      {item.submenu.map((subItem, subIndex) => (
                        <Link 
                          key={subIndex}
                          to={subItem.href}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {subItem.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              id="mobile-menu-button"
              type="button"
              className="text-gray-500 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-expanded={menuOpen}
              aria-haspopup="true"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {menuOpen ? (
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation using react-burger-menu */}
        <div className="md:hidden">
          <Menu
            isOpen={menuOpen}
            onStateChange={handleStateChange}
            styles={burgerStyles}
            customBurgerIcon={false}
            customCrossIcon={false}
            width={'80%'}
            right
          >
            {renderMobileMenuItems()}
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
